import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viCalendar, viPlus } from "@sippy-platform/valkyrie";

import { IconButton, useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function HolidaySetList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const holidaySetStore = useSelector((state) => state.domain.holidaySets);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time holidaySetStore or searchQuery changes
  const searchResults = useSearch(holidaySetStore.list, searchValue, ["name", "exten"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getHolidaySets()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && holidaySetStore.meta.lastFetched) {
      if (holidaySetStore.list.length >= 1) {
        navigate(`${holidaySetStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, holidaySetStore.list, holidaySetStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Holiday sets"
        aria-label="Holiday set list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => x.exten,
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
          icon: viCalendar,
        }}
        actions={
          <IconButton component={NavLink} to={"../../../timetables?tab=holidays"}>
            <Valkyrie icon={viArrowLeft} />
          </IconButton>
        }
        hideContentLoader={holidaySetStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: holidaySetStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any holiday sets yet",
          noResults: "No holiday sets in your current query",
          fetching: "Getting your holiday sets",
          icon: viCalendar,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default HolidaySetList;
